import { FC } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import styles from './AnalyticsExportChart.module.scss';

export interface AnalyticsExportPieSlice {
  label: string;
  percent: number;
  color: string;
}

/** Matches AnalyticsExportChart: fixed logical size, SVG stretched to its box. */
const LOGICAL_SIZE = 620;
const LABEL_RADIUS_RATIO = 0.65;

interface SliceLabelGeometry {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  percent: number;
}

/**
 * recharts places labels outside the slice and has no prop to pull them in, so
 * the position is worked out from the slice geometry it hands over.
 */
function renderSliceLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
}: SliceLabelGeometry) {
  const radians = -midAngle * (Math.PI / 180);
  const radius = outerRadius * LABEL_RADIUS_RATIO;

  return (
    <text
      x={cx + radius * Math.cos(radians)}
      y={cy + radius * Math.sin(radians)}
      fill="#ffffff"
      fontSize={34}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(percent)}%`}
    </text>
  );
}

interface Props {
  slices: AnalyticsExportPieSlice[];
}

const AnalyticsExportPieChart: FC<Props> = ({ slices }) => (
  <div className={styles.chart}>
    <PieChart width={LOGICAL_SIZE} height={LOGICAL_SIZE}>
      <Pie
        data={slices}
        dataKey="percent"
        nameKey="label"
        cx="50%"
        cy="50%"
        outerRadius="90%"
        isAnimationActive={false}
        stroke="none"
        label={renderSliceLabel}
        labelLine={false}
      >
        {slices.map((slice) => (
          <Cell key={slice.label} fill={slice.color} />
        ))}
      </Pie>
    </PieChart>
  </div>
);

export default AnalyticsExportPieChart;
